import { Button, DataTable, DataTableColDef, Tooltip, Typography, useCopyToClipboard } from '@chronosphereio/chrono-ui';
import { useMemo } from 'react';
import { ContextLinkConfig } from '@/plugin-system/model';
import { getVariableScope } from '../../model/links';
import { useContextLinkVariablesSuggestions } from '../context-links';

interface VariablesTableProps {
  contextLinkConfig?: ContextLinkConfig;
  isScopeColumnDisabled?: boolean;
}

interface VariableRow {
  variable: string;
  scope: string;
}

export function VariablesTable({ contextLinkConfig, isScopeColumnDisabled }: VariablesTableProps) {
  const variables = useContextLinkVariablesSuggestions({ contextLinkConfig });
  const copy = useCopyToClipboard();
  const rows = useMemo(() => {
    return (
      variables?.map((variable) => {
        const scope = getVariableScope(variable, contextLinkConfig);

        return { variable, scope: scope };
      }) || []
    );
  }, [variables, contextLinkConfig]);

  const columns = useMemo(() => {
    const baseColumns: Array<DataTableColDef<VariableRow>> = [
      {
        flex: 1,
        field: 'variable',
        headerName: 'Value',
        renderCell: (params) => {
          return (
            <Tooltip Content="Click to copy">
              <Button variant="tertiary" onClick={() => copy(`\${${params.value}}`)}>
                <Typography variant="code">{params.value}</Typography>
              </Button>
            </Tooltip>
          );
        },
      },
    ];

    if (!isScopeColumnDisabled) {
      baseColumns.push({
        width: 100,
        field: 'scope',
        headerName: 'Scope',
      });
    }

    return baseColumns;
  }, [isScopeColumnDisabled, copy]);

  return (
    <DataTable
      sx={{ border: (theme) => `1px solid ${theme.palette.divider}` }}
      autoHeight
      hideFooter
      getRowId={(row) => row.variable}
      rows={rows}
      columns={columns}
    />
  );
}
